import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, EyeOff, Thermometer, Droplets, Radio, Printer } from "lucide-react";
import { IMAGES } from "@/data/products";
import { usePlatform } from "@/context/PlatformContext";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/Primitives";

const SPOTS = [
  { id: "engine", label: "Engine bay", img: IMAGES.hiddenEngine, tip: "On the inner guard or strut tower, next to the fuse box — away from the engine block and exhaust heat." },
  { id: "dash", label: "Under the dash", img: IMAGES.hiddenDash, tip: "On the steel bracket behind the glovebox. Dry, cool and completely out of view." },
  { id: "bumper", label: "Behind the bumper", img: IMAGES.hiddenBumper, tip: "On the chassis crossmember inside the bumper or under the tray, shielded from road spray." },
  { id: "seat", label: "Under a seat", img: IMAGES.hiddenSeat, tip: "On the seat rail or frame — the easiest spot for cars, vans and utes." },
  { id: "trailer", label: "Trailer light bracket", img: IMAGES.hiddenTrailer, tip: "Inside the tail-light or mudguard bracket on a trailer or caravan." },
  { id: "spare", label: "Spare wheel well", img: IMAGES.hiddenSpare, tip: "Against the steel wall of the spare wheel well, under the boot floor." },
];

const RULES = [
  { icon: Thermometer, text: "Keep it away from direct engine, exhaust and brake heat." },
  { icon: Droplets, text: "Choose a dry, protected spot — avoid direct road spray and pressure washing." },
  { icon: Radio, text: "Steel shielding can reduce Bluetooth range. Deep inside an all-steel box, updates may be less frequent." },
];

const HiddenPlacement = () => {
  const [active, setActive] = useState(SPOTS[0]);
  const { config } = usePlatform();
  return (
    <section id="hidden" data-testid="hidden-placement-section" className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-tracksy-dark px-6 py-14 shadow-lift sm:px-10 lg:px-16 lg:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader dark eyebrow="Discreet by design" title="Out of sight. Still on the map." desc="At about 3 cm across, Tracksy tucks into the places nobody looks. Pick a spot, press it onto steel, close the panel." />
            <ul className="mt-8 space-y-2" data-testid="hidden-spot-list">
              {SPOTS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    data-testid={`hidden-spot-${s.id}`}
                    onMouseEnter={() => setActive(s)}
                    onClick={() => setActive(s)}
                    className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                      active.id === s.id ? "border-tracksy-orange/50 bg-white/10" : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    {active.id === s.id ? <Eye className="mt-0.5 h-4 w-4 shrink-0 text-tracksy-orange" /> : <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />}
                    <span>
                      <span className="block font-heading text-sm font-bold text-white">{s.label}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-white/60">{s.tip}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <Link to="/placement-guide" data-testid="hidden-guide-link" className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:border-tracksy-orange hover:text-tracksy-orange">
              <Printer className="h-3.5 w-3.5" /> Printable placement guide
            </Link>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.img
                    key={active.id}
                    data-testid="hidden-placement-image"
                    src={active.img}
                    alt={`Tracksy hidden ${active.label.toLowerCase()}`}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="aspect-[4/3] w-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute bottom-4 left-4 rounded-full bg-white/90 px-3.5 py-1.5 text-xs font-bold text-tracksy-ink backdrop-blur-xl">{active.label}</div>
              </div>
            </Reveal>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {RULES.map((r, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="flex gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                    <r.icon className="mt-0.5 h-4 w-4 shrink-0 text-tracksy-orange" />
                    <p className="text-xs leading-relaxed text-white/70">{r.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-white/50">
              Unlimited range. Tucked away or on the dashboard, your Tracksy stays findable through the {config.network} network — across town or across the world.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HiddenPlacement;
